/**
* NetWorthHero - Floating header + white data card
*/
import { ChevronLeft, ChevronRight, Lock, Info } from "lucide-react";
import { formatCurrencyWhole } from "../../utils/formatters";
import { usePlan } from "../../utils/plan"; // plan_09
import { useWealthView, ViewToggle } from "../wealth/WealthPaths"; // plan_22

const NetWorthHero = ({
  theme = 'pink',
  viewMonth,
  onPrevMonth,
  onNextMonth,
  canViewHistory,
  showFeaturePaywall,
  netWorth,
  totalAssets,
  totalDebts,
  projectedNetWorth,
  onInfoClick,
  hideTitle = false,
  chart = null, // plan_09
  monthChange = null, // plan_11
  path = null, // plan_22
}) => {
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  const plan = usePlan(); // plan_09
  const [wView, setWView] = useWealthView(); // plan_22

  // Theme-aware tile palette — sourced from Budget's Income/Spent/Saved-Inv/Leftover tiles
  // (BudgetHero.jsx) so Wealth's tiles stay in the same family per theme.
  // Debts: pink theme uses the blue (Goal) color; warm/blue themes use the pink (Income)
  // color instead. tileColors.coral is kept below in case it's needed again, but is
  // currently unused by any tile.
  const tileColors = {
    green: isPink
      ? { bg: 'linear-gradient(135deg, #E7F1DB 0%, #D3E5BE 100%)', text: '#5C7A4A' }
      : isWarm
      ? { bg: 'linear-gradient(135deg, #E9EFD9 0%, #DCE6C1 100%)', text: '#5C7A4A' }
      : { bg: 'linear-gradient(135deg, #DCEFF0 0%, #C9E4E6 100%)', text: '#2F7A80' },
    coral: { bg: 'linear-gradient(135deg, #FBE0DE 0%, #F5C9C6 100%)', text: '#B75450' },
    lavender: isPink
      ? { bg: 'linear-gradient(135deg, #E9E1F5 0%, #D5C7EB 100%)', text: '#7A5FB0' }
      : isWarm
      ? { bg: 'linear-gradient(135deg, #F0E3D9 0%, #E5D0C2 100%)', text: '#9A5B3C' }
      : { bg: 'linear-gradient(135deg, #E4E1F5 0%, #D2CDEB 100%)', text: '#6B5FB0' },
    blue: isPink
      ? { bg: 'linear-gradient(135deg, #DBE7F5 0%, #C4D6ED 100%)', text: '#3C6693' }
      : isWarm
      ? { bg: 'linear-gradient(135deg, #EDE3D5 0%, #E2D3BC 100%)', text: '#7A6A4A' }
      : { bg: 'linear-gradient(135deg, #E6E6F5 0%, #D6D6ED 100%)', text: '#5B4F93' },
    pink: isPink
      ? { bg: 'linear-gradient(135deg, #FAE3EC 0%, #F5D5E3 100%)', text: '#C23A72' }
      : isWarm
      ? { bg: 'linear-gradient(135deg, #F7E7D9 0%, #F0D8C2 100%)', text: '#B5651D' }
      : { bg: 'linear-gradient(135deg, #E1E8FA 0%, #D2DEF5 100%)', text: '#3A56C2' },
  };

  const shortMonthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const shortMonthName = shortMonthNames[viewMonth.getMonth()];
  const currentYear = viewMonth.getFullYear();
  const labelColor = isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';

  const handlePrev = () => canViewHistory ? onPrevMonth() : showFeaturePaywall('netWorthHistory');
  const handleNext = () => canViewHistory ? onNextMonth() : showFeaturePaywall('netWorthHistory');

  // plan_09: Money plan - net worth and its chart in one card, then the tiles.
  if (plan) {
    const card = { background: '#FFFFFF', borderRadius: 24, padding: 18, marginTop: 12 };
    const tile = (bg, ink, label, value) => (
      <div key={label} style={{ background: bg, borderRadius: 18, padding: '11px 12px', minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12, color: '#55525F' }}>{label}</span>
        <span style={{ display: 'block', fontSize: 16, fontWeight: 700, letterSpacing: '-0.4px', color: ink, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
      </div>
    );
    return (
      <div className="px-4 pb-2" data-testid="plan-networth-hero">
        {/* plan_11: like Funds and Invest - emoji, this-month chip, tiles inside */}
        <div style={{ ...card, borderRadius: 28, padding: '20px 18px 18px' }}>
          <div className="flex justify-between" style={{ gap: 14 }}>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, letterSpacing: '-0.3px', color: '#2B2C31' }}>Net worth</h2>
              <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-1px', color: '#2B2C31', marginTop: 4, lineHeight: 1.15 }}>{formatCurrencyWhole(netWorth)}</div>
              {monthChange !== null && monthChange !== 0 && (
                <span style={{ display: 'inline-block', marginTop: 8, background: monthChange > 0 ? '#DFF2E7' : '#FAE4ED', color: monthChange > 0 ? '#2F8756' : '#C04B7C', borderRadius: 999, padding: '5px 11px', fontSize: 12.5, fontWeight: 600 }}>
                  {monthChange > 0 ? '↗ ' : '↘ '}{formatCurrencyWhole(Math.abs(monthChange))} this month
                </span>
              )}
            </div>
            {path && chart ? <ViewToggle view={wView} onChange={setWView} /> : (
            <span className="flex items-center justify-center flex-shrink-0" style={{ width: 50, height: 50, borderRadius: 999, background: '#DFF2E7', fontSize: 23 }}>{'\u{1F331}'}</span>
            )}
          </div>
          {path && (wView === 'path' || !chart) ? <div style={{ marginTop: 6 }}>{path}</div> : chart ? <div style={{ marginTop: 14 }}>{chart}</div> : null}
          <div className="grid grid-cols-3" style={{ gap: 8, marginTop: 14 }}>
            {tile('#DFF2E7', '#2F8756', 'Assets', formatCurrencyWhole(totalAssets))}
            {tile('#DFE9F9', '#3C6DB5', 'Debts', `−${formatCurrencyWhole(totalDebts)}`)}
            {tile('#ECE6FA', '#7A5DD3', 'Projected', formatCurrencyWhole(projectedNetWorth))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`px-4 pb-2 ${hideTitle ? 'wy-ph wy-ph-cont' : 'pt-4'}`} style={{ fontFamily: "'DM Sans', sans-serif", '--ph-bleed': '0px', '--ph-inset': '1rem' }}>
      {/* Floating header - fully hidden when embedded; WealthHero owns title + month-nav in that case */}
      {!hideTitle && (
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '28px', fontWeight: 400, color: '#1A1A1F', letterSpacing: '-0.6px' }}>Net Worth</h1>
              <button onClick={() => onInfoClick && onInfoClick()} style={{ background: '#F4F1F8', border: '1px solid #EFEAF1', cursor: 'pointer', borderRadius: '50%', width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#6B6B73', padding: 0, flexShrink: 0 }}>i</button>
            </div>
            <div className="flex items-center bg-white border border-gray-200 rounded-full px-3 py-1.5 gap-1 shadow-sm">
              <button onClick={handlePrev} className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                {canViewHistory ? <ChevronLeft className="w-4 h-4 text-gray-600" /> : <Lock className="w-3 h-3 text-gray-400" />}
              </button>
              <span className="text-gray-700 text-xs font-semibold px-1 min-w-[32px] text-center">{shortMonthName}</span>
              <button onClick={handleNext} className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                {canViewHistory ? <ChevronRight className="w-4 h-4 text-gray-600" /> : <Lock className="w-3 h-3 text-gray-400" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Big number — floats directly on the page, no card */}
      <p className="hero-gradient-text" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '40px', fontWeight: 400, letterSpacing: '-1.5px', lineHeight: 1, marginBottom: '14px' }}>
        {formatCurrencyWhole(netWorth)}
      </p>

      {/* Stat tiles */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="rounded-[18px] wy-tile wy-tile-ink" style={{ background: tileColors.green.bg, padding: '12px 11px 10px' }}>
          <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="3" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '16px', marginBottom: '8px' }}>🏠</div>
          <span className="wy-tile-label" style={{ display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: tileColors.green.text, marginBottom: '3px' }}>Assets</span>
          <p className="wy-tile-value" style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.3px', color: tileColors.green.text }}>{formatCurrencyWhole(totalAssets)}</p>
        </div>
        <div className="rounded-[18px] wy-tile wy-tile-ink" style={{ background: isPink ? tileColors.blue.bg : tileColors.pink.bg, padding: '12px 11px 10px' }}>
          <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="2" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '16px', marginBottom: '8px' }}>📉</div>
          <span className="wy-tile-label" style={{ display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: isPink ? tileColors.blue.text : tileColors.pink.text, marginBottom: '3px' }}>Debts</span>
          <p className="wy-tile-value" style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.3px', color: isPink ? tileColors.blue.text : tileColors.pink.text }}>−{formatCurrencyWhole(totalDebts)}</p>
        </div>
        <div className="rounded-[18px] relative group wy-tile wy-tile-ink" style={{ background: tileColors.lavender.bg, padding: '12px 11px 10px' }}>
          <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="4" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '16px', marginBottom: '8px' }}>🔮</div>
          <span className="wy-tile-label" style={{ display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: tileColors.lavender.text, marginBottom: '3px' }}>Projected</span>
          <p className="wy-tile-value" style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.3px', color: tileColors.lavender.text }}>{formatCurrencyWhole(projectedNetWorth)}</p>
          <div className="absolute top-2 right-2">
            <div className="relative">
              <Info className="w-3 h-3 cursor-help" style={{ color: 'rgba(122,95,176,0.5)' }} />
              <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block w-40 p-2 bg-white text-gray-700 border border-gray-200 text-[9px] rounded-lg shadow-lg z-50">
                <p>Includes pending future income allocations</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetWorthHero;
